import { NgClass, NgStyle, isPlatformBrowser } from '@angular/common'
import type { ElementRef } from '@angular/core'
import {
  Component,
  HostListener,
  Input,
  PLATFORM_ID,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core'
import type { SafeHtml } from '@angular/platform-browser'
import { DomSanitizer } from '@angular/platform-browser'

import { TranslatePipe } from '@ngx-translate/core'

import { SVG_ICONS_INDEX } from '../../../../../../assets/icons/svg-icons'
import type { IndexItem } from '../../../../../types/types'

@Component({
  selector: 'dhp-index',
  templateUrl: './index.component.html',
  styleUrl: './index.component.scss',
  imports: [NgClass, NgStyle, TranslatePipe],
})
export class IndexComponent {
  private sanitizer = inject(DomSanitizer)

  // Two-way state: written both by this component (scroll tracking, toggling)
  // and readable by a parent. `model()` makes the write schedule change
  // detection, which a plain @Input field would not do under OnPush.
  readonly isExpanded = model<boolean>(false)
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input({ required: true }) indexItems!: IndexItem[] | null
  readonly articleTitle = input<string>('')
  readonly activeSection = model<string | null>(null)
  readonly isHeaderHidden = input<boolean>(false)
  readonly language = input<string>('nl')
  readonly isHeaderHiddenChange = output<boolean>()
  readonly indexItemClicked = output<string>()
  readonly articleContent =
    viewChild.required<ElementRef<HTMLElement>>('articleContent')

  readonly menuIcon: SafeHtml
  readonly closeIcon: SafeHtml
  readonly currentScroll = signal(0)
  private isBrowser: boolean
  private scrollTimeout: number | null = null

  constructor() {
    const platformId = inject(PLATFORM_ID)

    this.menuIcon = this.sanitizer.bypassSecurityTrustHtml(SVG_ICONS_INDEX.MENU)
    this.closeIcon = this.sanitizer.bypassSecurityTrustHtml(
      SVG_ICONS_INDEX.CLOSE
    )
    this.isBrowser = isPlatformBrowser(platformId)
  }

  toggleIndex(): void {
    this.isExpanded.update(expanded => !expanded)
  }

  onIndexItemClick(link: string): void {
    this.indexItemClicked.emit(link)
    if (window.innerWidth < 1024) {
      // Close index on mobile after clicking
      this.isExpanded.set(false)
    }
  }

  onIndexItemKeydown(event: KeyboardEvent, link: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.onIndexItemClick(link)
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.isBrowser) {
      // Update scroll position for index positioning
      this.currentScroll.set(window.scrollY)

      // Throttle the active section detection
      if (this.scrollTimeout) {
        window.cancelAnimationFrame(this.scrollTimeout)
      }

      this.scrollTimeout = window.requestAnimationFrame(() => {
        // Handle active section
        const headings = Array.from(
          document.querySelectorAll('h1, h2, h3, h4, h5, h6')
        )
        let activeHeading: Element | null = null

        for (const heading of headings) {
          const rect = heading.getBoundingClientRect()
          if (rect.top <= 100) {
            activeHeading = heading
          } else {
            break
          }
        }

        const newActiveSection = activeHeading?.id ?? null
        if (this.activeSection() !== newActiveSection) {
          this.activeSection.set(newActiveSection)
        }
      })
    }
  }
}
